import { useState } from 'react';
import { Bell, Cable, Save, ShieldX } from 'lucide-react';
import { useI18n, useTranslation } from '@solara/i18n';
import {
  Badge,
  Button,
  Card,
  CardBody,
  CardHeader,
  CardTitle,
  Input,
  LanguageToggle,
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
  ThemeToggle,
  useToast,
} from '@solara/ui-shared';
import { MapPin, ShieldCheck } from 'lucide-react';
import { useSettings, useUpdateSettings, useCompliancePosture } from '@solara/api-client/react';
import { useAuth, ROLE_LABEL } from '../auth/AuthContext';

export default function SettingsPage() {
  const { t } = useTranslation('admin');
  return (
    <div className="space-y-6">
      <header>
        <h1 className="text-2xl font-semibold tracking-tight">{t('settings.title')}</h1>
        <p className="text-fg-muted">{t('settings.subtitle')}</p>
      </header>
      <Tabs defaultValue="platform">
        <TabsList>
          <TabsTrigger value="platform">{t('settings.tabs.platform')}</TabsTrigger>
          <TabsTrigger value="integrations">{t('settings.tabs.integrations')}</TabsTrigger>
          <TabsTrigger value="notifications">{t('settings.tabs.notifications')}</TabsTrigger>
          <TabsTrigger value="preferences">{t('settings.tabs.preferences')}</TabsTrigger>
        </TabsList>
        <TabsContent value="platform"><PlatformTab /></TabsContent>
        <TabsContent value="integrations"><IntegrationsTab /></TabsContent>
        <TabsContent value="notifications"><NotificationsTab /></TabsContent>
        <TabsContent value="preferences"><PreferencesTab /></TabsContent>
      </Tabs>
    </div>
  );
}

function PlatformTab() {
  const { t } = useTranslation('admin');
  const { can } = useAuth();
  const { toast } = useToast();
  const settingsQuery = useSettings();
  const updateSettings = useUpdateSettings();
  void settingsQuery;
  const [tenantSlots, setTenantSlots] = useState('200');
  const [rateLimit, setRateLimit] = useState('100');
  const [batchSize, setBatchSize] = useState('1000');
  const [exportLimit, setExportLimit] = useState('10000');

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between gap-3">
          <CardTitle>{t('settings.platform.title')}</CardTitle>
          {!can('settings.write') ? (
            <span className="text-xs text-warning inline-flex items-center gap-1.5">
              <ShieldX className="h-3.5 w-3.5" />
              {t('common.platformAdminOnly')}
            </span>
          ) : null}
        </div>
      </CardHeader>
      <CardBody className="grid gap-4 sm:grid-cols-2">
        <Input
          label={t('settings.platform.tenantSlots')}
          hint={t('settings.platform.tenantSlotsHint')}
          value={tenantSlots}
          onChange={(e) => setTenantSlots(e.target.value)}
          inputMode="numeric"
          dir="ltr"
          disabled={!can('settings.write')}
        />
        <Input
          label={t('settings.platform.rateLimit')}
          hint={t('settings.platform.rateLimitHint')}
          value={rateLimit}
          onChange={(e) => setRateLimit(e.target.value)}
          inputMode="numeric"
          dir="ltr"
          disabled={!can('settings.write')}
        />
        <Input
          label={t('settings.platform.batchSize')}
          hint={t('settings.platform.batchSizeHint')}
          value={batchSize}
          onChange={(e) => setBatchSize(e.target.value)}
          inputMode="numeric"
          dir="ltr"
          disabled={!can('settings.write')}
        />
        <Input
          label={t('settings.platform.exportLimit')}
          hint={t('settings.platform.exportLimitHint')}
          value={exportLimit}
          onChange={(e) => setExportLimit(e.target.value)}
          inputMode="numeric"
          dir="ltr"
          disabled={!can('settings.write')}
        />
      </CardBody>
      <div className="px-6 pb-6 flex justify-end">
        <Button
          onClick={async () => {
            try {
              await updateSettings.mutateAsync({
                tenantSlots: Number(tenantSlots) || undefined,
                rateLimitRps: Number(rateLimit) || undefined,
                batchSize: Number(batchSize) || undefined,
                exportLimit: Number(exportLimit) || undefined,
              });
              toast({ title: t('settings.platform.saved'), tone: 'success' });
            } catch (err) {
              toast({ title: err instanceof Error ? err.message : 'Save failed', tone: 'danger' });
            }
          }}
          disabled={!can('settings.write')}
          className="gap-2"
        >
          <Save className="h-4 w-4" />
          {t('settings.platform.save')}
        </Button>
      </div>
    </Card>
  );
}

function IntegrationsTab() {
  const { t } = useTranslation('admin');
  const { can } = useAuth();
  const posture = useCompliancePosture();
  const fields: { key: 'tickstarUrl' | 'emarataxUrl' | 'kafkaBootstrap' | 'redisUrl' | 'minioUrl'; placeholder: string }[] = [
    { key: 'tickstarUrl',   placeholder: 'https://api.tickstar.com/solara' },
    { key: 'emarataxUrl',   placeholder: 'https://emaratax.fta.gov.ae/api/v2' },
    { key: 'kafkaBootstrap', placeholder: 'kafka-1.solara.local:9092,kafka-2.solara.local:9092' },
    { key: 'redisUrl',      placeholder: 'redis://redis-cluster.solara.local:6379' },
    { key: 'minioUrl',      placeholder: 'https://s3.me-central-1.amazonaws.com/solara-worm' },
  ];
  const dr = posture.data?.dataResidency;
  return (
    <div className="space-y-4">
      {/* Data residency — FTA audit checkpoint (MoF Decision 64/2025).
          Explicitly name the AWS UAE region; Bahrain (me-south-1) is excluded. */}
      {dr ? (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-base">
              <MapPin className="h-4 w-4 text-success" />
              Data residency
            </CardTitle>
          </CardHeader>
          <CardBody className="space-y-3 text-sm">
            <div className="flex flex-wrap items-center gap-2">
              <Badge tone="success" className="gap-1">
                <ShieldCheck className="h-3.5 w-3.5" />
                {dr.provider} · {dr.region}
              </Badge>
              <span className="text-fg-muted">{dr.regionName}</span>
            </div>
            <p className="text-fg-muted">{dr.note}</p>
            <div className="text-xs text-fg-subtle">
              Not permitted: {dr.excludedRegions.join(' · ')}
            </div>
          </CardBody>
        </Card>
      ) : null}
      <Card>
        <CardBody className="grid gap-4 sm:grid-cols-2">
          {fields.map((f) => (
            <Input
              key={f.key}
              label={t(`settings.integrations.${f.key}`)}
              placeholder={f.placeholder}
              disabled={!can('settings.write')}
              dir="ltr"
              className="font-mono text-xs"
            />
          ))}
          <Input
            label="Object storage region (WORM archive)"
            value={dr ? `${dr.provider} ${dr.region} — ${dr.regionName}` : ''}
            readOnly
            dir="ltr"
            className="font-mono text-xs"
          />
        </CardBody>
      </Card>
    </div>
  );
}

function NotificationsTab() {
  const { t } = useTranslation('admin');
  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Bell className="h-5 w-5" />
          {t('settings.notifications.title')}
        </CardTitle>
      </CardHeader>
      <CardBody className="space-y-4 text-sm">
        <Channel
          label={t('settings.notifications.email')}
          hint={t('settings.notifications.emailHint')}
          tone="success"
          stateLabel="Enabled"
        />
        <Channel
          label={t('settings.notifications.slack')}
          hint={t('settings.notifications.slackHint')}
          tone="success"
          stateLabel="Enabled"
        />
        <Channel
          label={t('settings.notifications.pagerduty')}
          hint={t('settings.notifications.pagerdutyHint')}
          tone="warning"
          stateLabel="Setup pending"
        />
      </CardBody>
    </Card>
  );
}

function PreferencesTab() {
  const { t } = useTranslation('admin');
  const { locale } = useI18n();
  const { currentUser } = useAuth();

  return (
    <div className="grid gap-4 sm:grid-cols-2">
      <Card>
        <CardHeader>
          <CardTitle>{t('settings.preferences.title')}</CardTitle>
        </CardHeader>
        <CardBody className="space-y-3 text-sm">
          <Row label={t('settings.preferences.language')}>
            <LanguageToggle />
          </Row>
          <Row label={t('settings.preferences.theme')}>
            <ThemeToggle />
          </Row>
          <Row label="Locale">
            <code className="rounded bg-surface-muted px-2 py-1 text-xs">{locale}</code>
          </Row>
          <Row label="Role">
            <Badge tone="brand">{ROLE_LABEL[currentUser.role]}</Badge>
          </Row>
          <Row label="Email">
            <code className="text-xs" dir="ltr">{currentUser.email}</code>
          </Row>
        </CardBody>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Cable className="h-5 w-5" />
            {currentUser.team}
          </CardTitle>
        </CardHeader>
        <CardBody className="text-sm text-fg-muted">
          You are signed in as <strong>{currentUser.givenName} {currentUser.familyName}</strong>.
          Use the role switcher in the header to demo Platform Admin / Support Agent flows.
        </CardBody>
      </Card>
    </div>
  );
}

function Channel({
  label,
  hint,
  tone,
  stateLabel,
}: {
  label: string;
  hint: string;
  tone: 'success' | 'warning' | 'danger';
  stateLabel: string;
}) {
  return (
    <div className="flex items-start justify-between gap-3 rounded-xl border border-border p-3">
      <div>
        <div className="font-medium">{label}</div>
        <div className="text-xs text-fg-muted">{hint}</div>
      </div>
      <Badge tone={tone}>{stateLabel}</Badge>
    </div>
  );
}

function Row({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="flex items-center justify-between gap-3">
      <span className="text-fg-muted">{label}</span>
      {children}
    </div>
  );
}
